import { useFeatureGate } from '@trello/feature-gate-client';

import type { DoneStateSource } from './CardDoneState';

const individualSourcesSet = new Set(['Planner', 'YourCards']);
const workspaceSourcesSet = new Set(['Board', 'Inbox', 'Workspace']);

const isIndividualSource = (source: DoneStateSource) =>
  individualSourcesSet.has(source);

const isWorkspaceSource = (source: DoneStateSource) =>
  workspaceSourcesSet.has(source);

/**
 * Return true if the card done state should be shown
 *
 * @remarks
 * This hook checks if the new card done state feature is enabled for the individual or workspace, based on the source.
 *
 * @param cardSource - The source using the card done state
 * @returns true if the new card done state feature is enabled, false otherwise
 *
 */
export const useShouldShowCardDoneState = (
  cardSource: DoneStateSource | undefined,
) => {
  const { value: isNewCardDoneStateFeatureEnabledForIndividuals } =
    useFeatureGate('phx_new_card_done_state');
  const { value: isNewCardDoneStateFeatureEnabledForWorkspaces } =
    useFeatureGate('phx_new_card_done_state_workspace');

  const sourceToCheck = cardSource || 'Board';

  const isIndividualFeatureEnabled =
    isNewCardDoneStateFeatureEnabledForIndividuals &&
    isIndividualSource(sourceToCheck);

  const isWorkspaceFeatureEnabled =
    isNewCardDoneStateFeatureEnabledForWorkspaces &&
    isWorkspaceSource(sourceToCheck);

  return isIndividualFeatureEnabled || isWorkspaceFeatureEnabled;
};
